import React, { useState, useEffect } from 'react';
import { veoCreditsService, VeoCreditBalance } from '../../services/veoCreditsService';
import VeoCreditHistoryModal from './VeoCreditHistoryModal';
import styles from './VeoCreditIndicator.module.css';

interface VeoCreditIndicatorProps {
  onRefresh?: () => void;
}

const VeoCreditIndicator: React.FC<VeoCreditIndicatorProps> = ({ onRefresh }) => {
  const [creditBalance, setCreditBalance] = useState<VeoCreditBalance | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadCreditData();
  }, []);

  const loadCreditData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const balance = await veoCreditsService.getCreditBalance();

      setCreditBalance(balance);
    } catch (err) {
      console.error('Error loading VEO credit data:', err);
      setError('Failed to load VEO credit information');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadCreditData(); // Refresh balance
    onRefresh?.(); // Trigger parent refresh
  };

  const getStatusColor = (credits: number): string => {
    if (credits >= 20) return '#00d4ff'; // Cyan - plenty
    if (credits >= 10) return '#10b981'; // Green - good
    if (credits >= 5) return '#f59e0b'; // Orange - low
    return '#ef4444'; // Red - critical
  };

  const getStatusText = (credits: number): string => {
    if (credits >= 20) return 'Fully Stocked';
    if (credits >= 10) return 'Well Supplied';
    if (credits >= 5) return 'Running Low';
    if (credits > 0) return 'Critical Level';
    return 'Depleted';
  };

  // Don't render if user has never purchased VEO credits
  if (!loading && (!creditBalance || creditBalance.totalPurchased === 0)) {
    return null;
  }

  if (loading) {
    return (
      <div className={styles.veoCard}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <span className={styles.loadingText}>Scanning VEO Systems...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.veoCard}>
        <div className={styles.errorContainer}>
          <span className={styles.errorIcon}>⚠️</span>
          <span className={styles.errorText}>{error}</span>
          <button className={styles.retryButton} onClick={loadCreditData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!creditBalance) return null;

  const statusColor = getStatusColor(creditBalance.credits);
  const statusText = getStatusText(creditBalance.credits);

  return (
    <>
      <div className={styles.veoCard}>
        {/* Futuristic Header */}
        <div className={styles.cardHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.veoIcon}>
              <span className={styles.iconSymbol}>🎬</span>
              <div className={styles.iconGlow}></div>
            </div>
            <div className={styles.headerInfo}>
              <h3 className={styles.title}>VEO PREMIUM CREDITS</h3>
              <div className={styles.subtitle}>
                <span className={styles.tech}>Google AI • 8-Second Generation</span>
              </div>
            </div>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.statusIndicator} style={{ backgroundColor: statusColor }}>
              <div className={styles.statusPulse}></div>
            </div>
          </div>
        </div>

        {/* Credit Balance Display */}
        <div className={styles.balanceSection}>
          <div className={styles.balanceContainer}>
            <div className={styles.creditCount} style={{ color: statusColor }}>
              {veoCreditsService.formatCredits(creditBalance.credits)}
            </div>
            <div className={styles.creditLabel}>CREDITS AVAILABLE</div>
          </div>
          <div className={styles.statusText} style={{ color: statusColor }}>
            {statusText}
          </div>
        </div>

        {/* Stats Grid */}
        <div className={styles.statsGrid}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{creditBalance.totalConsumed}</div>
            <div className={styles.statLabel}>Generated</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{creditBalance.totalPurchased}</div>
            <div className={styles.statLabel}>Purchased</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{veoCreditsService.formatPrice(creditBalance.totalSpent)}</div>
            <div className={styles.statLabel}>Total Spent</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={styles.actionSection}>
          <button 
            className={styles.purchaseButton}
            onClick={() => {
              // Navigate to pricing page with hash
              window.location.href = '/pricing#veo-section';
              // After navigation, scroll to the section
              setTimeout(() => {
                const section = document.getElementById('veo-section');
                if (section) {
                  section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
          >
            <span className={styles.buttonIcon}>💳</span>
            <span>Acquire Credits</span>
            <div className={styles.buttonGlow}></div>
          </button>
          
          <button 
            className={styles.historyButton}
            onClick={() => setShowHistoryModal(true)}
          >
            <span className={styles.buttonIcon}>📊</span>
            <span>Transaction Log</span>
          </button>
        </div>

        {/* Futuristic Effects */}
        <div className={styles.cardEffects}>
          <div className={styles.scanLine}></div>
          <div className={styles.cornerAccents}>
            <div className={styles.corner}></div>
            <div className={styles.corner}></div>
            <div className={styles.corner}></div>
            <div className={styles.corner}></div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <VeoCreditHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        userId={creditBalance.userId}
      />
    </>
  );
};

export default VeoCreditIndicator;
